import React, { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  FileItemType,
  GalleryItemType,
  InitialState,
  LinkItemType,
  ParamsType,
  SimpleTaskType,
} from './types'
import { useDispatch, useSelector } from 'react-redux'
import * as actions from './actions'
import { useHistory } from 'react-router-dom'
import { BlockType } from '@famiryru/react-notion-renderer/src/types'
import moment from 'moment'
import { declOfNum } from '../../utils/helpers'
import { useAlert } from 'react-alert'

// Components
import Header from '../../components/Header'
import Footer from '../../components/Footer'
import LoadingPage from '../../components/LoadingPage'
import Progress from '../../components/ProgressBlock'
import AssistantBlock from '../../components/AssistantBlock'
import DescriptionBlock from '../../components/DescriptionBlock'
import TaskBlock from '../../components/TaskBlock'
import GalleryBlock from '../../components/GalleryBlock'
import ButtonDownload from '../../components/ButtonDownload'
import ButtonLink from '../../components/ButtonLink'
import ButtonPrev from '../../components/ButtonPrev'
import ButtonComplete from '../../components/ButtonComplete'
import ButtonNext from '../../components/ButtonNext'
import ModalLinks from '../../components/ModalLinks'
import ModalFiles from '../../components/ModalFiles'
import SimpleTaskBlock from '../../components/SimpleTaskBlock'

// Styles
import './styles.scss'

export default () => {
  const [remaining, setRemaining] = useState<number>(0)
  const [lessonIsComplete, setLessonIsComplete] = useState<boolean>(true)
  const [allowExecNext, setAllowExecNext] = useState<boolean>(false)
  // const [notionDescription, setNotionDescription] = useState<BlockType[]>([])
  const [notionTask, setNotionTask] = useState<BlockType[]>([])
  const [gallery, setGallery] = useState<GalleryItemType[]>([])
  const [links, setLinks] = useState<LinkItemType[]>([])
  const [files, setFiles] = useState<FileItemType[]>([])
  const [simpleTask, setSimpleTask] = useState<SimpleTaskType | null>(null)

  const [pageLoaded, setPageLoaded] = useState<boolean>(false)

  const [modalLinks, setModalLinks] = useState<boolean>(false)
  const [modalFiles, setModalFiles] = useState<boolean>(false)
  const [initialModuleFinished, setInitialModuleFinished] =
    useState<boolean>(false)

  const history = useHistory()
  const { id, mid, lid } = useParams<ParamsType>()
  const dispatch = useDispatch()
  const alert = useAlert()
  const isMounted = useRef<boolean>(false)
  const hasInitialModule = useRef<boolean>(false)

  const {
    page,
    isProcessComplete,
    simpleTaskStatus,
    isFetching,
    isProcessSimpleTaskStatus,
    isProcessSendSimpleTask,
    isProcessJoinSimpleTask,
    error,
    module,
    progress,
    isFetchingModule,
    isProcessUserState,
  } = useSelector(
    (state: { moduleLessonPage: InitialState }) => state.moduleLessonPage
  )

  useEffect(() => {
    if (error) {
      alert.error(error)
    }
  }, [error])

  useEffect(() => {
    dispatch(actions.destroy())
    dispatch(actions.fetchModuleLessonPage(id, mid, lid))
    isMounted.current = true
    return () => {
      dispatch(actions.destroy())
    }
  }, [])

  useEffect(() => {
    if (page && isMounted.current) {
      const userLesson = page.user_lesson
      if (userLesson) {
        setLessonIsComplete(!!userLesson.finished_at)
        if (userLesson.remaining_seconds_to_study > 0) {
          setRemaining(userLesson.remaining_seconds_to_study)
        } else {
          setRemaining(0)
        }
      }

      if (page.pivot.module.is_finished && !hasInitialModule.current) {
        setInitialModuleFinished(true)
      }

      hasInitialModule.current = true

      if (page.blocks) {
        // const blockNotionDescription = page.blocks.find(
        //   block => block.type === 'notion' && block.tag === 'short'
        // )?.notion.notion_page_content
        const blockNotionTask = page.blocks.find(
          block => block.type === 'notion' && !block.tag
        )?.notion.notion_page_content
        const blockGallery = page.blocks.find(block => block.type === 'gallery')
          ?.gallery?.images
        const blockLinks = page.blocks.find(block => block.type === 'links')
          ?.links?.links
        const blockFiles = page.blocks.find(block => block.type === 'files')
          ?.files?.files
        const simpleTaskBlock = page.blocks.find(
          block => block.type === 'simple-task'
        )

        if (blockNotionTask) {
          setNotionTask([...JSON.parse(blockNotionTask)])
        }

        // if (blockNotionDescription) {
        //   setNotionDescription([...JSON.parse(blockNotionDescription)])
        // }

        if (simpleTaskBlock?.simple_task) {
          setSimpleTask(simpleTaskBlock.simple_task)
          dispatch(
            actions.fetchSimpleTaskStatus(
              id,
              mid,
              lid,
              simpleTaskBlock.simple_task.simple_task_id
            )
          )
        }

        setGallery(blockGallery || [])
        setLinks(blockLinks || [])
        setFiles(blockFiles || [])
        setPageLoaded(true)
      }
    }
  }, [page])

  useEffect(() => {
    if (allowExecNext) {
      if (module?.user_module) {
        const moduleIsCompleted = !!module?.user_module.finished_at
        if (moduleIsCompleted) {
          const moduleLastScreenId = module.module_screens?.find(
            module => module.type === 'last'
          )?.module_screen_id
          if (moduleLastScreenId && !initialModuleFinished) {
            history.push(
              `/courses/${id}/modules/${mid}/screens/${moduleLastScreenId}`
            )
          } else {
            if (page && page.pivot.is_last) {
              history.push(`/courses/${id}`)
            } else {
              history.push(`/courses/${id}/modules/${mid}`)
            }
          }
        } else {
          history.push(`/courses/${id}/modules/${mid}`)
        }
      } else {
        alert.error('Вы не вступили в урок!')
      }
    }
  }, [module])

  const handleLessonComplete = () => {
    dispatch(actions.setLessonComplete(id, mid, lid))
  }

  const handleLessonNext = () => {
    setPageLoaded(false)
    setAllowExecNext(true)
    dispatch(actions.fetchModule(id, mid))
  }

  const durationAsString = (start: string, end: string): string => {
    const duration = moment.duration(moment(end).diff(moment(start)))

    const hours = duration.hours()
    const hoursFormatted = `${hours} ${declOfNum(hours, [
      'ЧАС',
      'ЧАСА',
      'ЧАСОВ',
    ])} `

    const minutes = duration.minutes()
    const minutesFormatted = `${minutes} ${declOfNum(minutes, [
      'МИНУТУ',
      'МИНУТЫ',
      'МИНУТ',
    ])} `

    // const seconds = duration.seconds()
    // const secondsFormatted = `${seconds} ${declOfNum(seconds, [
    //   'СЕКУНДУ',
    //   'СЕКУНДЫ',
    //   'СЕКУНД',
    // ])}`

    return [hoursFormatted, minutesFormatted].join('')
  }

  return (
    <>
      <Header />
      <div className={'container'}>
        {(!isMounted.current ||
          isFetching ||
          isFetchingModule ||
          isProcessUserState ||
          isProcessSimpleTaskStatus) &&
        !pageLoaded ? (
          <LoadingPage />
        ) : (
          <>
            <Progress
              title={progress?.title ? 'Прогресс курса: ' : 'Загрузка...'}
              percent={progress?.progress?.other?.percent || 0}
              style={{ marginBottom: 30 }}
            />
            <DescriptionBlock
              title={page?.title || ''}
              // notionBlocks={notionDescription}
              style={{ marginBottom: 40 }}
              showButton={false}
              showVideo={false}
            />
            {page?.user_lesson?.finished_at ? (
              <AssistantBlock
                background={'#DEEFDE'}
                text={`ЭТОТ УРОК БЫЛ ПРОЙДЕН <b>${moment(
                  page.user_lesson?.finished_at
                ).format('DD.MM.YYYY')}</b> ЗА <b>${durationAsString(
                  page.user_lesson?.created_at,
                  page.user_lesson?.finished_at
                )}</b>`}
                style={{ marginBottom: 40 }}
                isWinking={true}
              />
            ) : page?.pivot?.assistant_caption ? (
              <AssistantBlock
                background={'#F2F2F2'}
                text={page.pivot.assistant_caption}
                style={{ marginBottom: 40 }}
                isWinking={false}
              />
            ) : null}
            <TaskBlock
              showIcon={false}
              notionBlocks={notionTask}
              style={{ marginBottom: 40 }}
            />
            <GalleryBlock style={{ marginBottom: 48 }} gallery={gallery} />
            {simpleTask && simpleTaskStatus ? (
              <>
                <div className={'lesson__task'}>
                  <div className={'lesson__task-item'}>
                    <SimpleTaskBlock
                      simpleTask={simpleTask}
                      simpleTaskStatus={simpleTaskStatus}
                      courseId={id}
                      moduleId={mid}
                      lessonId={lid}
                      isProcess={isProcessSendSimpleTask}
                      isProcessJoin={isProcessJoinSimpleTask || isFetching}
                    />
                  </div>
                  <div className={'lesson__task-item'}>
                    <ButtonDownload
                      disabled={!files?.length}
                      title={'Скачать<br />необходимые<br/ >файлы'}
                      onClick={() => setModalFiles(true)}
                    />
                    <ButtonLink
                      disabled={!links?.length}
                      title={'посмотреть<br />полезные<br />ссылки'}
                      onClick={() => setModalLinks(true)}
                    />
                  </div>
                </div>
                <div className={'lesson__task-buttons'}>
                  <div className={'lesson__task-buttons-item'}>
                    <ButtonPrev
                      onClick={() =>
                        history.push(`/courses/${id}/modules/${mid}`)
                      }
                      style={{ margin: 0 }}
                    />
                  </div>
                  <div className={'lesson__task-buttons-item'}>
                    <ButtonComplete
                      counter={remaining}
                      title={'Все изучил!'}
                      onClick={handleLessonComplete}
                      disabled={
                        lessonIsComplete ||
                        (!simpleTaskStatus.is_finished &&
                          simpleTaskStatus.task_required)
                      }
                      isLoading={isProcessComplete || isFetching}
                      style={{ margin: 0 }}
                    />
                  </div>
                  <div className={'lesson__task-buttons-item'}>
                    <ButtonNext
                      onClick={handleLessonNext}
                      disabled={!lessonIsComplete}
                      style={{ margin: 0 }}
                    />
                  </div>
                </div>
              </>
            ) : (
              <div className={'lesson__buttons'}>
                <div className={'lesson__buttons-left'}>
                  <ButtonComplete
                    counter={remaining}
                    title={'Все изучил!'}
                    onClick={handleLessonComplete}
                    disabled={lessonIsComplete}
                    isLoading={isProcessComplete || isFetching}
                  />
                  <div className={'item'}>
                    <ButtonPrev
                      onClick={() =>
                        history.push(`/courses/${id}/modules/${mid}`)
                      }
                    />
                  </div>
                  <div className={'item'}>
                    <ButtonNext
                      onClick={handleLessonNext}
                      disabled={!lessonIsComplete}
                    />
                  </div>
                </div>
                <div className={'lesson__buttons-center'} />
                <div className={'lesson__buttons-right'}>
                  <div className={'item'}>
                    <ButtonDownload
                      disabled={!files?.length}
                      title={'Скачать<br />необходимые<br/ >файлы'}
                      onClick={() => setModalFiles(true)}
                    />
                  </div>
                  <div className={'item'}>
                    <ButtonLink
                      disabled={!links?.length}
                      title={'посмотреть<br />полезные<br />ссылки'}
                      onClick={() => setModalLinks(true)}
                    />
                  </div>
                </div>
              </div>
            )}
            {error ? (
              <span
                style={{ color: 'red', marginBottom: '10px', display: 'block' }}
              >
                {error}
              </span>
            ) : null}
            <Progress
              title={progress?.title ? 'Прогресс курса: ' : 'Загрузка...'}
              percent={progress?.progress?.other?.percent || 0}
              style={{ marginBottom: 50 }}
            />
            <ModalFiles
              isOpen={modalFiles}
              title={'Необходимые файлы'}
              items={files}
              onClose={() => setModalFiles(false)}
            />
            <ModalLinks
              isOpen={modalLinks}
              title={'Полезные ссылки'}
              items={links}
              onClose={() => setModalLinks(false)}
            />
          </>
        )}
      </div>
      <Footer />
    </>
  )
}
